import { useTrackingContextStore } from '@/analytics/trackingContextStore'
import { useAuthStore } from '@/store/authStore'
import { clearSessionQueries } from '@/lib/queryClient'
import { tokenService } from '@/services/tokenService'
import { SELECTED_ORGANIZATION_ID_KEY } from '@/lib/sessionStorageKeys'

const AUTH_LOGOUT_EVENT = 'auth:logout'

/**
 * Removes persisted credentials and session-scoped selections from localStorage.
 * Does not touch non-session keys (e.g. `locale`, theme).
 */
export async function clearAuthStorage(): Promise<void> {
  await tokenService.clearTokens()
  try {
    localStorage.removeItem(SELECTED_ORGANIZATION_ID_KEY)
  } catch {
    // ignore quota / private mode
  }
}

function resetInMemoryTrackingContext(): void {
  try {
    useTrackingContextStore.getState().setContext({
      user_id: null,
      org_id: null,
      role: null,
      feature_name: null,
      page_id: null,
      page_name: null,
    })
  } catch {
    // ignore
  }
}

/**
 * Clears persisted auth data, React Query cache, auth store (tokens, user, OTP session),
 * analytics tracking context, then broadcasts `auth:logout` so other listeners
 * (profile mode, order draft persist, duplicate query clear) run the same path as token expiry.
 */
export async function finalizeLocalSessionEnd(reason: string): Promise<void> {
  await clearAuthStorage()
  clearSessionQueries()
  resetInMemoryTrackingContext()
  useAuthStore.getState().clearAuth()

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(AUTH_LOGOUT_EVENT, {
        detail: { reason },
      })
    )
  }
}
